import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { api, formatApiErrorDetail } from '@/lib/api';
import PageMeta from '@/components/PageMeta';
import { FORMS } from '@/constants/testIds';

const INTERESTS = [
  'Recovery or rehabilitation',
  'Everyday movement or strength',
  'Physiotherapy or allied health',
  'Hospital or clinic',
  'Fitness or coaching platform',
  'Research',
  'Partnership or investment',
  'Media',
  'Other',
];

export default function EarlyAccess() {
  const T = FORMS.earlyAccess;
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    country: '',
    primary_interest: INTERESTS[0],
    message: '',
    consent: false,
  });
  const [state, setState] = useState({ submitting: false, success: false, error: '' });

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    if (state.submitting) return;
    setState({ submitting: true, success: false, error: '' });
    try {
      await api.post('/early-access', form);
      setState({ submitting: false, success: true, error: '' });
      setForm({
        first_name: '', last_name: '', email: '', country: '',
        primary_interest: INTERESTS[0], message: '', consent: false,
      });
    } catch (err) {
      setState({
        submitting: false,
        success: false,
        error: formatApiErrorDetail(err.response?.data?.detail) || 'Submission failed. Please try again.',
      });
    }
  }

  return (
    <div>
      <PageMeta
        title="Join early access"
        description="Register your interest in early access to Valanti Labs. Product updates, testing opportunities, research news and launch information."
      />
      <section className="vl-container py-16 lg:py-24 max-w-3xl">
        <p className="mono-label text-vl-violet">Early access</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-vl-ink">
          Be part of the next stage.
        </h1>
        <p className="mt-6 text-lg text-vl-ink/70 leading-relaxed">
          Join the early access list for product updates, testing opportunities, research news, and future launch
          information.
        </p>
      </section>

      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-16 lg:py-20 max-w-3xl">
          {state.success ? (
            <div data-testid={T.success} className="rounded-3xl bg-vl-surface border hairline p-10 text-center">
              <CheckCircle2 className="mx-auto text-[#4C7C59]" size={36} />
              <h2 className="font-serif-display text-3xl text-vl-ink mt-4">Thank you.</h2>
              <p className="mt-4 text-vl-ink/75 leading-relaxed max-w-lg mx-auto">
                Your interest has been recorded. Early-access participation is limited, and submitting this form does
                not guarantee selection. We will contact you when a suitable opportunity becomes available.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid={T.form}
              className="rounded-3xl bg-vl-surface border hairline p-7 sm:p-10 space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="First name" required>
                  <input
                    data-testid={T.firstName}
                    value={form.first_name}
                    onChange={set('first_name')}
                    required
                    className={inputCls}
                  />
                </Field>
                <Field label="Last name" required>
                  <input
                    data-testid={T.lastName}
                    value={form.last_name}
                    onChange={set('last_name')}
                    required
                    className={inputCls}
                  />
                </Field>
              </div>
              <Field label="Email" required>
                <input
                  type="email"
                  data-testid={T.email}
                  value={form.email}
                  onChange={set('email')}
                  required
                  className={inputCls}
                />
              </Field>
              <Field label="Country" required>
                <input
                  data-testid={T.country}
                  value={form.country}
                  onChange={set('country')}
                  required
                  className={inputCls}
                />
              </Field>
              <Field label="Primary interest" required>
                <select
                  data-testid={T.interest}
                  value={form.primary_interest}
                  onChange={set('primary_interest')}
                  required
                  className={inputCls}
                >
                  {INTERESTS.map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </Field>
              <Field label="Optional message">
                <textarea
                  data-testid={T.message}
                  value={form.message}
                  onChange={set('message')}
                  rows={4}
                  className={inputCls + ' resize-none'}
                  placeholder="Anything you'd like us to know (please don't include sensitive health information)."
                />
              </Field>

              <label className="flex items-start gap-3 text-sm text-vl-ink/75 leading-relaxed">
                <input
                  type="checkbox"
                  data-testid={T.consent}
                  checked={form.consent}
                  onChange={set('consent')}
                  className="mt-1 h-4 w-4 rounded border-vl-ink/30 text-vl-ink focus:ring-vl-violet"
                />
                <span>
                  I consent to Valanti Labs storing the information above so that we can contact me about early
                  access. I understand the application is in pre-release preparation and submitting this form does not
                  guarantee selection.
                </span>
              </label>

              {state.error && (
                <p data-testid={T.error} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {state.error}
                </p>
              )}

              <button
                type="submit"
                data-testid={T.submit}
                disabled={!form.consent || state.submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-full bg-vl-ink text-vl-bg px-7 py-3.5 text-sm font-medium hover:bg-vl-violet transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {state.submitting ? 'Sending…' : 'Register your interest'}
              </button>

              <p className="text-xs text-vl-ink/55 leading-relaxed">
                We do not ask for diagnosis, medication, medical history or treatment information at this stage. Please
                do not include sensitive health information in the optional message.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls =
  'w-full rounded-xl border border-vl-ink/15 bg-transparent px-4 py-3 text-vl-ink placeholder:text-vl-ink/40 focus:outline-none focus:border-vl-violet focus:ring-1 focus:ring-vl-violet transition-colors';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-vl-ink mb-2">
        {label} {required && <span className="text-vl-violet">*</span>}
      </span>
      {children}
    </label>
  );
}
